import { useEffect, useState } from "react";
import {
    Box, Typography, TextField, Button, CircularProgress,
    Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, Paper, Link, Alert
} from "@mui/material";
import moment from "moment";
import { Update } from "@material-ui/icons";
import { withFormCache } from "./FormCacheResources";
import { onRequestFailure } from "./components/AuthHandler";

// Define initial state for all form fields to be cached
const initialState = {
    email: ""
};

const InvoicesForm = ({
    onResponse,
    onInvoiceSelect,
    formState,
    setEmail,
    clearForm,
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [invoices, setInvoices] = useState([]);
    const [customerId, setCustomerId] = useState(null);
    const [noClockMessage, setNoClockMessage] = useState(null);
    const [subscriptionId, setSubscriptionId] = useState(null);
    const [showAdvanceButtons, setShowAdvanceButtons] = useState(false);
    const [alert, setAlert] = useState({ message: "", severity: "info" });

    useEffect(() => {
        // Only sync if the formState email changes from external sources
        // This prevents infinite loops with onChange
    }, [formState.email]);

    useEffect(() => {
        if (alert.message) {
            const timer = setTimeout(() => {
                setAlert({ message: "", severity: "info" });
            }, 6000);
            return () => clearTimeout(timer);
        }
    }, [alert]);

    const handleEmailChange = (event) => {
        setEmail(event.target.value);
        setInvoices([]);
        setNoClockMessage(null);
        onInvoiceSelect(null);
    };

    const handleClear = () => {
        clearForm();
        setInvoices([]);
        setNoClockMessage(null);
        setShowAdvanceButtons(false);
        onInvoiceSelect(null);
        setAlert({ message: "", severity: "info" });
    };

    const makeFetchRequest = async (url, options, actionDescription) => {
        const date = new Date();
        const time = `[${date.getHours()}:${date.getMinutes()}:${date.getSeconds()}] `;
        onResponse(`${time}Request Sent to: ${url}`);

        try {
            const response = await fetch(url, options);
            if (response.status === 403 || response.status === 401) {
                onRequestFailure();
                throw new Error(`${response.status} ${response.statusText}`);
            }
            const data = await response.json();
            onResponse(`${time}Response for ${actionDescription}: ${response.status}(${response.statusText})`);
            if (!response.ok) throw new Error(`Non-200 response from Stripe for ${actionDescription}`);
            return data;
        } catch (error) {
            onResponse(`${time}Error sending request for ${actionDescription}: ${error.message}`);
            throw error;
        }
    };

    const findCustomer = async () => {
        const q = encodeURIComponent(`email:"${formState.email}"`);
        const url = `https://api.stripe.com/v1/customers/search?query=${q}`;
        const options = {
            method: "GET",
            headers: {
                Authorization: `Bearer ${process.env.REACT_APP_STRIPE_KEY}`,
                "Content-Type": "application/x-www-form-urlencoded",
                "Stripe-Version": "2025-02-24.acacia"
            }
        };
        const data = await makeFetchRequest(url, options, "customer search");
        if (data.data.length === 0) {
            setAlert({ message: "No matching customer found", severity: "error" });
            throw new Error("No matching customer found");
        }
        setCustomerId(data.data[0].id)
        return data.data[0];
    };

    const getSubscription = async (customerId) => {
        const url = `https://api.stripe.com/v1/subscriptions?customer=${customerId}`;
        const options = {
            method: "GET",
            headers: {
                Authorization: `Bearer ${process.env.REACT_APP_STRIPE_KEY}`,
                "Content-Type": "application/x-www-form-urlencoded",
                "Stripe-Version": "2025-02-24.acacia"
            }
        };
        const data = await makeFetchRequest(url, options, "subscription");
        if (data.data.length === 0) {
            setAlert({ message: "No subscriptions found for this customer", severity: "error" });
            throw new Error("No subscriptions found");
        }
        setSubscriptionId(data.data[0].id);
        return data.data[0];
    };

    const getClock = async (clockId) => {
        const url = `https://api.stripe.com/v1/test_helpers/test_clocks/${clockId}`;
        const options = {
            method: "GET",
            headers: {
                Authorization: `Bearer ${process.env.REACT_APP_STRIPE_KEY}`,
                "Content-Type": "application/x-www-form-urlencoded",
                "Stripe-Version": "2025-02-24.acacia"
            }
        };
        return await makeFetchRequest(url, options, "test clock");
    };

    const advanceClock = async (clock, seconds) => {
        const formData = new URLSearchParams();
        formData.append("frozen_time", clock.frozen_time + seconds);
        const url = `https://api.stripe.com/v1/test_helpers/test_clocks/${clock.id}/advance`;
        const options = {
            method: "POST",
            headers: {
                Authorization: `Bearer ${process.env.REACT_APP_STRIPE_KEY}`,
                "Content-Type": "application/x-www-form-urlencoded",
                "Stripe-Version": "2025-02-24.acacia"
            },
            body: formData
        };
        const data = await makeFetchRequest(url, options, "advancing clock");
        const newTime = moment.unix(data.frozen_time).format("YYYY-MM-DD HH:mm:ss [UTC]");
        setAlert({ message: `Clock advanced to ${newTime}. You may need to wait a minute and then refresh invoices.`, severity: "success" });
    };

    const timeTravel = async (seconds) => {
        setIsLoading(true);
        try {
            const subscription = await getSubscription(customerId);
            if (!subscription.test_clock) setNoClockMessage({ subscriptionId: subscription.id });
            else {
                const clock = await getClock(subscription.test_clock);
                await advanceClock(clock, seconds);
            }
        } catch (error) {
            console.error(error)
        } finally {
            setIsLoading(false)
        }
    };

    const listInvoices = async (customerId) => {
        const url = `https://api.stripe.com/v1/invoices?customer=${customerId}`;
        const options = {
            method: "GET",
            headers: {
                Authorization: `Bearer ${process.env.REACT_APP_STRIPE_KEY}`,
                "Content-Type": "application/x-www-form-urlencoded",
                "Stripe-Version": "2025-02-24.acacia"
            }
        };
        const data = await makeFetchRequest(url, options, "invoices");
        if (data.data.length === 0) {
            setAlert({ message: "No invoices found for this customer", severity: "error" });
            throw new Error("No invoices found");
        }
        return data.data;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if(!formState.email.trim()) {
            setAlert({ message: "Please enter a valid email address", severity: "error" });
            return;
        }
        try {
            setIsLoading(true);
            const customer = await findCustomer();
            const invoicesData = await listInvoices(customer.id);
            invoicesData.sort((a, b) => b.created - a.created);
            setInvoices(invoicesData);
            setNoClockMessage(null);
            setAlert({ message: "", severity: "info" });
            onInvoiceSelect(null);
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleRowClick = (invoice) => { onInvoiceSelect(invoice) };
    const toggleAdvanceButtons = () => { setShowAdvanceButtons((prev) => !prev) };

    return (
        <Box sx={{ fontFamily: "'Arial', sans-serif", padding: "20px", width: "100%" }}>
            <Typography variant="h3" sx={{ color: "#333", fontSize: "1.5rem", marginBottom: "15px", borderBottom: "2px solid #eee", paddingBottom: "5px" }}>Invoices</Typography>
            <Box component="form" onSubmit={handleSubmit}
                sx={{
                    backgroundColor: "#f8f9fa",
                    padding: "15px",
                    borderRadius: "4px",
                    marginTop: "10px",
                    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                    display: "flex",
                    flexDirection: "column",
                    gap: "15px"
                }}
            >
                <TextField
                    label="Customer Email"
                    name="email"
                    type="email"
                    value={formState.email}
                    onChange={handleEmailChange}
                    placeholder="Enter customer email"
                    variant="outlined"
                    fullWidth
                    sx={{ backgroundColor: "white" }}
                    required
                />
                <Button type="submit" variant="contained" color="primary" disabled={isLoading}>Get Invoices</Button>
                <Button variant="contained" color="secondary" onClick={toggleAdvanceButtons} disabled={isLoading} startIcon={<Update />}>{!showAdvanceButtons ? "Advance Clocks" : "Hide"}</Button>
                {showAdvanceButtons && (
                    <Box sx={{ display: "flex", justifyContent: "center", gap: "10px", flexWrap: "wrap" }}>
                        <Button variant="contained" color="primary" onClick={() => timeTravel(60 * 60)} disabled={isLoading}>Advance 1 Hour</Button>
                        <Button variant="contained" color="primary" onClick={() => timeTravel(24 * 60 * 60)} disabled={isLoading}>Advance 1 Day</Button>
                        <Button variant="contained" color="primary" onClick={() => timeTravel(31 * 24 * 60 * 60)} disabled={isLoading}>Advance 31 Days</Button>
                    </Box>
                )}
                <Button type="button" variant="outlined" onClick={handleClear} disabled={isLoading}>Clear</Button>
            </Box>
            <Box sx={{ marginTop: "20px", maxWidth: "100%" }}>
                {isLoading && <div style={{ textAlign: 'center', marginLeft: '3em' }}><CircularProgress /></div>}
                {alert.message &&
                    <div style={{ textAlign: 'center', marginLeft: '3em' }}>
                        <Alert severity={alert.severity} sx={{ mb: 2 }}>{alert.message}</Alert>
                    </div>
                }
                {noClockMessage && (
                    <Box sx={{ backgroundColor: "#fff3cd", padding: "15px", borderRadius: "4px", border: "1px solid #ffeeba", mb: 2, overflowWrap: "break-word" }}>
                        <Typography color="textSecondary">
                            No test clock found on this customer's subscription. Share the following link with someone with Stripe access to create one:
                        </Typography>
                        <Typography component="code" sx={{ display: "block", my: 1, fontFamily: "monospace" }}>
                            <Link href={`https://dashboard.stripe.com/test/subscriptions/${noClockMessage.subscriptionId}`} target="_blank" rel="noopener">
                                https://dashboard.stripe.com/test/subscriptions/{noClockMessage.subscriptionId}
                            </Link>
                        </Typography>
                    </Box>
                )}
                {invoices.length > 0 && (
                    <TableContainer component={Paper} sx={{ boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)" }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: "bold" }}>Invoice ID</TableCell>
                                    <TableCell sx={{ fontWeight: "bold" }}>Invoice Number</TableCell>
                                    <TableCell sx={{ fontWeight: "bold" }}>Created At</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {invoices.map((invoice) => (
                                    <TableRow key={invoice.id} onClick={() => handleRowClick(invoice)} sx={{ cursor: "pointer", "&:hover": { backgroundColor: "#f5f5f5" } }}>
                                        <TableCell>{invoice.id}</TableCell>
                                        <TableCell>{invoice.number}</TableCell>
                                        <TableCell>{moment.unix(invoice.created).format("YYYY-MM-DD")}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Box>
        </Box>
    );
};

export default withFormCache(InvoicesForm, 'invoicesForm', initialState);